// src/cockpit4/components/ProductDispatcherPanel.tsx
// ============================================
// 产品调度中心（v5.2 审计修复 P2-③ · 白皮书第十八章⑨ Product Dispatcher）
//
// 定位：按产品线调度 Runtime——每个软件资产（酒店/量化/命理/增长工具）对应一个 Runtime，
//       这里统一展示产品状态并提供"查看详情 / 启动编排"入口。
// 数据：真实调用 RuntimeManager.query（产品 Runtime 的 catalog），禁止 Mock。
// ============================================

import { useState, useCallback } from "react";
import { Store, Play, Loader2, Eye, Rocket, Activity, ArrowRight } from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface ProductDispatchItem {
  id: string;
  name: string;
  runtime: RuntimeName;
  category: string;
  color: string;
  desc: string;
  /** 编排入口：点击触发 GrowthFactory（仅 GEO+DSO）或查询详情 */
  orchestratable?: boolean;
}

const PRODUCTS: ProductDispatchItem[] = [
  { id: "hotel", name: "Hotel SaaS", runtime: RuntimeName.Hotel, category: "SaaS", color: "#38BDF8", desc: "酒店日报/入住率/收入预测/运营建议" },
  { id: "gold", name: "Gold Factory", runtime: RuntimeName.GoldFactory, category: "工厂", color: "#F59E0B", desc: "工厂建模/MES集成/订单管理" },
  { id: "destiny", name: "AI Destiny", runtime: RuntimeName.AIDestiny, category: "知识", color: "#A78BFA", desc: "八字/紫微/奇门/择日" },
  { id: "quant", name: "量化交易", runtime: RuntimeName.Quant, category: "量化", color: "#10B981", desc: "行情/持仓/策略/风控" },
  { id: "geo_dso", name: "GEO+DSO 增长工具", runtime: RuntimeName.GrowthFactory, category: "增长", color: "#06B6D4", desc: "9 stage 编排：新闻→知识→GEO→DSO→内容→发布→线索→收入", orchestratable: true },
];

export default function ProductDispatcherPanel() {
  const [detail, setDetail] = useState<{ name: string; info: any } | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<string | null>(null);

  const waitInit = useCallback(async () => {
    for (let i = 0; i < 100; i++) {
      if (RuntimeManager.isInitialized()) break;
      await new Promise((r) => setTimeout(r, 200));
    }
    return RuntimeManager.isInitialized();
  }, []);

  // 查看产品详情（真实 catalog）
  const handleView = async (p: ProductDispatchItem) => {
    setLoading(p.id);
    setDetail(null);
    try {
      await waitInit();
      if (!RuntimeManager.hasRuntime(p.runtime)) {
        setDetail({ name: p.name, info: { error: "Runtime 未注册" } });
        return;
      }
      const resp = await RuntimeManager.query(p.runtime, { type: "catalog" } as any);
      const inner = resp?.data as any;
      const data = inner?.success ? inner.data : inner;
      setDetail({ name: p.name, info: data || resp });
    } catch (err: any) {
      setDetail({ name: p.name, info: { error: err?.message || String(err) } });
    } finally {
      setLoading(null);
    }
  };

  // 启动编排（GEO+DSO 9 stage / 其他产品待接入）
  const handleRun = async (p: ProductDispatchItem) => {
    setRunning(p.id);
    setRunResult(null);
    try {
      await waitInit();
      if (p.orchestratable && RuntimeManager.hasRuntime(RuntimeName.GrowthFactory)) {
        const resp = await RuntimeManager.execute(RuntimeName.GrowthFactory, "run_daily_growth");
        const inner = resp?.data as any;
        const pipeline = inner?.pipeline || inner?.data?.pipeline;
        const completed = pipeline?.completedStages ?? "?";
        const total = pipeline?.totalStages ?? "?";
        setRunResult(`✅ ${p.name} 编排完成：${completed}/${total} stage 成功`);
      } else {
        setRunResult(`ℹ️ ${p.name} 暂无编排入口，请在对应工作区操作`);
      }
    } catch (err: any) {
      setRunResult(`❌ 编排失败：${err?.message || String(err)}`);
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="h-full overflow-auto p-4 space-y-3">
      <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03] p-3">
        <div className="flex items-start gap-2">
          <Rocket size={13} className="text-cyan-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[11px] font-medium text-gray-300">产品调度中心（Product Dispatcher）</p>
            <p className="text-[9px] font-mono text-gray-600 leading-relaxed mt-0.5">
              按产品线调度 Runtime：软件资产统一入口（白皮书第五章软件资产 + 第十八章⑨）。
              每个产品对应一个 Runtime，可查看真实状态、触发编排。
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {PRODUCTS.map((p) => {
          const Icon = Store;
          return (
            <div key={p.id} className="rounded-xl border p-3" style={{ borderColor: `${p.color}22`, background: `${p.color}06` }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${p.color}18`, border: `1px solid ${p.color}33` }}>
                    <Icon size={14} style={{ color: p.color }} />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium" style={{ color: p.color }}>{p.name}</div>
                    <div className="text-[8px] font-mono text-gray-600">{p.category} · {p.runtime}</div>
                  </div>
                </div>
                {p.orchestratable && (
                  <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/25">可编排</span>
                )}
              </div>
              <p className="text-[9px] font-mono text-gray-500 leading-relaxed mb-2 min-h-[28px]">{p.desc}</p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handleView(p)}
                  disabled={loading === p.id}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-gray-300 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition disabled:opacity-40"
                >
                  {loading === p.id ? <Loader2 size={9} className="animate-spin" /> : <Eye size={9} />}
                  查看详情
                </button>
                {p.orchestratable && (
                  <button
                    onClick={() => handleRun(p)}
                    disabled={running === p.id}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 transition disabled:opacity-40"
                  >
                    {running === p.id ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />}
                    启动编排
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {runResult && (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.03] p-2.5 text-[10px] font-mono text-emerald-300">
          {runResult}
        </div>
      )}

      {detail && (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-gray-300 flex items-center gap-1.5">
              <Activity size={11} className="text-cyan-400" /> {detail.name} · Runtime 详情
            </span>
            <button onClick={() => setDetail(null)} className="text-gray-600 hover:text-gray-300 text-[10px]">关闭</button>
          </div>
          <pre className="text-[10px] font-mono text-gray-400 leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto">
            {JSON.stringify(detail.info, null, 2).slice(0, 2000)}
          </pre>
        </div>
      )}
    </div>
  );
}
